import { ref } from 'vue'
import { ApiError } from '@/api/client'

/**
 * Avisos de resultado de una acción, arriba a la derecha.
 *
 * Cada pantalla resolvía esto por su cuenta con un `VAlert` propio: el mensaje
 * aparecía donde estuviera montado ese alert —a veces fuera de la vista, a veces
 * empujando el formulario— y cada una elegía su color. Guardar, borrar o
 * publicar son la misma clase de evento en todo el portal, así que el aviso es
 * uno solo y sale siempre en el mismo lugar.
 *
 * Es para el **resultado de una acción**. Lo que no va acá: los errores de carga
 * de una pantalla (son el estado de la página, no un evento) y la validación de
 * un campo (va pegada al campo).
 *
 * La cola es un ref de módulo, no un store de Pinia: no tiene que persistir,
 * hidratarse ni leerse fuera de `AppSnackbar`.
 */
export interface SnackbarNotice {
  text: string

  /** Color del tema: `success` | `error` | `warning` | `info`. */
  color: string

  /**
   * Milisegundos en pantalla. Se usa para lo que hay que **leer** y no solo
   * registrar —un aviso de contexto como la zona horaria del centro— donde los
   * cinco segundos del resto no alcanzan.
   */
  timeout?: number
}

export const snackbarQueue = ref<SnackbarNotice[]>([])

const push = (text: string, color: string, timeout?: number) => {
  if (text)
    snackbarQueue.value = [...snackbarQueue.value, { text, color, timeout }]
}

export const notifySuccess = (text: string) => push(text, 'success')
export const notifyError = (text: string) => push(text, 'error')
export const notifyWarning = (text: string) => push(text, 'warning')
export const notifyInfo = (text: string, timeout?: number) => push(text, 'info', timeout)

/**
 * `catch (e) { notifyError(apiMessage(e, 'Could not save.')) }`
 *
 * Solo se muestra el texto de un `ApiError` —lo que el backend redactó para el
 * usuario—; cualquier otra excepción cae al mensaje de respaldo en vez de
 * exponer un "Network Error" o un stack.
 */
export const apiMessage = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? e.message : fallback
